import { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Typography } from '@web-chat-core/ui-kit';
import { useUserSettingsContext } from 'contexts';
import { FEATURES } from 'consts';
import { useManageVideoDevices } from 'features/PreJoin/hooks/useManageVideoDevices';
import { VirtualBackgroundButtonWithText } from 'features/VirtualBackground/components/VirtualBackgroundModalButtonWithText/VirtualBackgroundButtonWithText';
import { isFeatureOn } from 'useCase/featureFlags/featureFlagsApi';
import { selectRoom, selectIsPreJoin } from 'state/selectors';
import { Section } from '../../components/Section/Section';
import { Selector } from '../../components/Selector/Selector';
import { getVideoDevicesOptions, getActiveVideoDevice } from '../../helper';
import { VirtualBackgroundsButtonWrapper, NoPermissionHint } from './view';

type DeviceOption = {
    label: string;
    value: string;
};

export const CameraSection = observer(() => {
    const { t } = useTranslation();
    const room = selectRoom();
    const isPreJoin = selectIsPreJoin();

    const { videoDevices, videoDeviceId, permissions } = useUserSettingsContext();
    const isCameraAvailable = permissions.video;
    const { selectVideoDevice } = useManageVideoDevices();

    const [isSwitching, setIsSwitching] = useState<boolean>(false);

    const isVirtualBackgroundsEnabled = isFeatureOn(FEATURES.VCS_VIRTUAL_BACKGROUNDS_ENABLED);

    const options = useMemo(() => getVideoDevicesOptions({ devices: videoDevices }), [videoDevices]);

    const value = useMemo(() => {
        const active = getActiveVideoDevice({ devices: videoDevices, currentDeviceId: videoDeviceId });
        return active ? { label: active.label, value: active.deviceId } : null;
    }, [videoDevices, videoDeviceId]);

    const onChange = useCallback(
        (camera: DeviceOption) => {
            if (isPreJoin) {
                selectVideoDevice({ deviceId: camera.value } as MediaDeviceInfo);
                return;
            }

            if (!room) return;

            setIsSwitching(true);
            room.switchActiveDevice('videoinput', camera.value)
                .then(() => {
                    selectVideoDevice({ deviceId: camera.value } as MediaDeviceInfo);
                })
                .catch((e) => {
                    console.warn('Error switching videoinput device', e);
                })
                .finally(() => {
                    setIsSwitching(false);
                });
        },
        [room, isPreJoin, selectVideoDevice],
    );

    const isDisabled = isSwitching || !isCameraAvailable;

    return (
        <Section title={t('vks_microphone_camera')} withSeparatorBefore>
            <Selector
                isSearchable={false}
                closeMenuOnSelect={false}
                options={options}
                value={value}
                isDisabled={isDisabled}
                onChange={onChange}
                placeholder={t('vks_microphone_devices_unavailable')}
                width={'100%'}
                cy={'camerasSelect'}
            />
            {!isCameraAvailable && (
                <NoPermissionHint>
                    <Typography variant="caption3" colorVariant="danger">
                        {t('vks_microphone_camera_permission_settings')}
                    </Typography>
                </NoPermissionHint>
            )}
            {isVirtualBackgroundsEnabled && (
                <VirtualBackgroundsButtonWrapper>
                    <VirtualBackgroundButtonWithText />
                </VirtualBackgroundsButtonWrapper>
            )}
        </Section>
    );
});

CameraSection.displayName = 'CameraSection';
